import { Accordion, Badge, Col, Form, Row, Table } from 'react-bootstrap';

// jewelry_rental/itemsOrProducts/ListANewPiece - a structured, category-aware spec sheet for a
// jewellery listing. Everything here is OPTIONAL (title / price / location / category stay
// required in ItemForm); an owner can list a piece with none of it filled in.
//
// All values are stored flat on the item's generic `attributes` bag (Mixed), so no backend
// schema change is needed - listingAttributesSchema already accepts string / number / boolean /
// string[] values. `setComponents` is persisted as a string[] ("1 Necklace", "2 Bangles").

export const JEWELLERY_CATEGORIES = [
  'Necklace', 'Necklace Set', 'Earrings', 'Maang Tikka', 'Nath', 'Bangles',
  'Bracelet', 'Ring', 'Anklet', 'Waist Belt / Kamarband', 'Hair Accessories',
  'Bridal Set', 'Other'
];

// Exported so the browse-page filters (ItemList.js) stay in lockstep with the listing form.
export const PRIMARY_COLOURS = ['Gold', 'Silver', 'Rose Gold', 'Antique Gold', 'Other'];
export const FINISHES = ['Glossy', 'Matte', 'Antique', 'Oxidized', 'Other'];
export const STONE_WORK_TYPES = ['Kundan', 'CZ', 'AD', 'Pearl', 'Meenakari', 'Beaded', 'American Diamond', 'Other'];
const CONDITIONS = ['Brand New', 'Like New', 'Excellent', 'Good', 'Minor Wear'];
const BANGLE_SIZES = ['2.2', '2.4', '2.6', '2.8', '2.10', '2.12'];
const WEIGHT_UNITS = ['g', 'kg'];
const SET_COMPONENTS = ['Necklace', 'Earrings', 'Maang Tikka', 'Nath', 'Bangles', 'Bracelet', 'Ring', 'Kamarband', 'Pendant', 'Chain', 'Other'];

// Section 4 - which measurements are relevant per category. A category not listed here shows no
// size fields at all ("For categories where a measurement isn't relevant, don't display it").
const DIMENSION_FIELDS = {
  'Necklace': ['length', 'width', 'adjustable'],
  'Necklace Set': ['length', 'width', 'adjustable'],
  'Bridal Set': ['length', 'width', 'adjustable'],
  'Bangles': ['bangleSize', 'innerDiameter'],
  'Ring': ['ringSize', 'adjustable'],
  'Earrings': ['length', 'width', 'earringWeight'],
  'Maang Tikka': ['pendantSize', 'chainLength'],
  'Nath': ['diameter', 'adjustable'],
  'Bracelet': ['length', 'innerDiameter', 'adjustable'],
  'Anklet': ['length', 'adjustable']
};

// attribute key <-> dimension slug. Only the keys for the selected category are written out, so
// switching category doesn't leave a stale "ring size" on a necklace.
const DIM_ATTR = {
  length: 'dimLength', width: 'dimWidth', adjustable: 'dimAdjustable',
  bangleSize: 'bangleSize', innerDiameter: 'innerDiameter', ringSize: 'ringSize',
  pendantSize: 'pendantSize', chainLength: 'chainLength', diameter: 'diameter',
  earringWeight: 'earringWeight'
};
const DIM_LABEL = {
  length: 'Length', width: 'Width', adjustable: 'Adjustable',
  bangleSize: 'Bangle size', innerDiameter: 'Inner diameter', ringSize: 'Ring size',
  pendantSize: 'Pendant size', chainLength: 'Chain length', diameter: 'Diameter',
  earringWeight: 'Weight'
};

export function dimensionFieldsFor(category) {
  return DIMENSION_FIELDS[category] || [];
}
export function isSetCategory(category) {
  return /\bset\b/i.test(category || '');
}

// Flat form keys this section owns - spread into ItemForm's emptyForm.
export const jewellerySpecDefaults = {
  brand: '', collectionName: '',
  primaryColour: '', finish: '', stoneWorkType: '', stoneColour: '', pearlColour: '', designStyle: '',
  dimLength: '', dimWidth: '', dimAdjustable: '', bangleSize: '', innerDiameter: '',
  ringSize: '', pendantSize: '', chainLength: '', diameter: '', earringWeight: '',
  setComponents: [], setCompleteOnly: false,
  approxWeight: '', weightUnit: 'g',
  condition: '', scratches: '', missingStones: '', repairHistory: '', otherDefects: '',
  // '' = not insured (default); 'inclusive' = cost in rental price; 'exclusive' = separate charge
  insurancePricing: ''
};

// "2 Bangles" -> { name: 'Bangles', qty: 2 }
function parseSetComponent(str) {
  const m = String(str).trim().match(/^(\d+)\s+(.*)$/);
  return m ? { name: m[2], qty: Number(m[1]) } : { name: String(str).trim(), qty: 1 };
}

export function jewellerySpecFromItem(item = {}) {
  const a = item.attributes || {};
  return {
    brand: a.brand || '',
    collectionName: a.collectionName || '',
    // section 3's "Primary Colour" supersedes the old free "metal" field - fall back to it so
    // pieces listed before this change keep their value.
    primaryColour: a.primaryColour || a.metal || '',
    finish: a.finish || '',
    stoneWorkType: a.stoneWorkType || '',
    stoneColour: a.stoneColour || '',
    pearlColour: a.pearlColour || '',
    designStyle: a.designStyle || '',
    dimLength: a.dimLength ?? '',
    dimWidth: a.dimWidth ?? '',
    dimAdjustable: a.dimAdjustable || '',
    bangleSize: a.bangleSize || '',
    innerDiameter: a.innerDiameter ?? '',
    ringSize: a.ringSize ?? '',
    pendantSize: a.pendantSize ?? '',
    chainLength: a.chainLength ?? '',
    diameter: a.diameter ?? '',
    earringWeight: a.earringWeight ?? '',
    setComponents: Array.isArray(a.setComponents) ? a.setComponents.map(parseSetComponent) : [],
    setCompleteOnly: !!a.setCompleteOnly,
    approxWeight: a.approxWeight ?? '',
    weightUnit: a.weightUnit || 'g',
    condition: a.condition || '',
    scratches: a.scratches || '',
    missingStones: a.missingStones || '',
    repairHistory: a.repairHistory || '',
    otherDefects: a.otherDefects || '',
    // Backwards compat: old records stored a plain boolean insured:true with no pricing type.
    // Treat legacy insured:true as 'inclusive' so existing listings don't silently lose their value.
    insurancePricing: a.insurancePricing || (a.insured === true ? 'inclusive' : '')
  };
}

export function jewellerySpecToAttributes(form) {
  const out = {};
  const put = (k, v) => {
    const val = typeof v === 'string' ? v.trim() : v;
    if (val !== '' && val != null) out[k] = val;
  };

  put('brand', form.brand);
  put('collectionName', form.collectionName);

  if (form.primaryColour) {
    out.primaryColour = form.primaryColour;
    // keep the legacy keys other surfaces (JewelleryCard, BookingDetail) still read.
    out.metal = form.primaryColour;
    out.metalType = form.primaryColour;
  }
  put('finish', form.finish);
  put('stoneWorkType', form.stoneWorkType);
  put('stoneColour', form.stoneColour);
  put('pearlColour', form.pearlColour);
  put('designStyle', form.designStyle);

  // Only the measurements that apply to this category are written. Any dimension key that is NOT
  // relevant to the current category is explicitly blanked, so re-categorising a piece (e.g. Ring
  // -> Necklace) doesn't leave a stale "ring size" behind after the PATCH merge.
  const relevant = new Set(dimensionFieldsFor(form.category).map((slug) => DIM_ATTR[slug]));
  Object.values(DIM_ATTR).forEach((attr) => {
    if (relevant.has(attr)) put(attr, form[attr]);
    else out[attr] = '';
  });

  const comps = (form.setComponents || [])
    .filter((c) => c.name && c.name.trim())
    .map((c) => `${Math.max(1, Number(c.qty) || 1)} ${c.name.trim()}`);
  if (comps.length) {
    out.setComponents = comps;
    if (form.setCompleteOnly) out.setCompleteOnly = true;
  }

  if (form.approxWeight !== '' && form.approxWeight != null && !Number.isNaN(Number(form.approxWeight))) {
    out.approxWeight = Number(form.approxWeight);
    out.weightUnit = form.weightUnit || 'g';
  }

  put('condition', form.condition);
  put('scratches', form.scratches);
  put('missingStones', form.missingStones);
  put('repairHistory', form.repairHistory);
  put('otherDefects', form.otherDefects);

  // insured stays as a boolean for backwards compat with any API/display code that checks it.
  // insurancePricing carries the richer value: '' | 'inclusive' | 'exclusive'.
  out.insured = !!form.insurancePricing;
  out.insurancePricing = form.insurancePricing || null;
  return out;
}

// ---------------------------------------------------------------------------
// The editor - grouped into skippable accordion sections (all optional).
// ---------------------------------------------------------------------------
export default function JewellerySpecFields({ form, setForm }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setChecked = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const dims = dimensionFieldsFor(form.category);
  const showSet = isSetCategory(form.category);

  const toggleComponent = (name) => (e) => setForm((p) => {
    const has = p.setComponents.some((c) => c.name === name);
    return {
      ...p,
      setComponents: e.target.checked
        ? (has ? p.setComponents : [...p.setComponents, { name, qty: 1 }])
        : p.setComponents.filter((c) => c.name !== name)
    };
  });
  const setComponentQty = (name) => (e) => setForm((p) => ({
    ...p,
    setComponents: p.setComponents.map((c) => (c.name === name ? { ...c, qty: Math.max(1, Number(e.target.value) || 1) } : c))
  }));
  const componentQty = (name) => form.setComponents.find((c) => c.name === name)?.qty;

  const renderDimField = (slug) => {
    if (slug === 'adjustable') {
      return (
        <Col md={4} key={slug}>
          <Form.Group className="mb-2" controlId="dim-adjustable">
            <Form.Label>Adjustable</Form.Label>
            <Form.Select value={form.dimAdjustable} onChange={set('dimAdjustable')}>
              <option value="">—</option>
              <option>Yes</option>
              <option>No</option>
            </Form.Select>
          </Form.Group>
        </Col>
      );
    }
    if (slug === 'bangleSize') {
      return (
        <Col md={4} key={slug}>
          <Form.Group className="mb-2" controlId="dim-bangleSize">
            <Form.Label>Bangle size</Form.Label>
            <Form.Select value={BANGLE_SIZES.includes(form.bangleSize) ? form.bangleSize : (form.bangleSize ? 'Other' : '')} onChange={set('bangleSize')}>
              <option value="">—</option>
              {BANGLE_SIZES.map((s) => <option key={s}>{s}</option>)}
              <option>Other</option>
            </Form.Select>
          </Form.Group>
        </Col>
      );
    }
    const attr = DIM_ATTR[slug];
    return (
      <Col md={4} key={slug}>
        <Form.Group className="mb-2" controlId={`dim-${attr}`}>
          <Form.Label>{DIM_LABEL[slug]}</Form.Label>
          <Form.Control
            placeholder={slug === 'earringWeight' ? 'e.g. 12 g' : 'e.g. 16 in / 40 cm'}
            value={form[attr]}
            onChange={set(attr)}
          />
        </Form.Group>
      </Col>
    );
  };

  return (
    <Accordion alwaysOpen className="mb-3">
      <Accordion.Item eventKey="appearance">
        <Accordion.Header>Appearance &amp; make <span className="text-muted ms-2 small">optional</span></Accordion.Header>
        <Accordion.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Primary colour</Form.Label>
                <Form.Select value={PRIMARY_COLOURS.includes(form.primaryColour) ? form.primaryColour : (form.primaryColour ? 'Other' : '')} onChange={set('primaryColour')}>
                  <option value="">—</option>
                  {PRIMARY_COLOURS.map((c) => <option key={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Finish</Form.Label>
                <Form.Select value={FINISHES.includes(form.finish) ? form.finish : (form.finish ? 'Other' : '')} onChange={set('finish')}>
                  <option value="">—</option>
                  {FINISHES.map((c) => <option key={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Stone / work type</Form.Label>
                <Form.Select value={STONE_WORK_TYPES.includes(form.stoneWorkType) ? form.stoneWorkType : (form.stoneWorkType ? 'Other' : '')} onChange={set('stoneWorkType')}>
                  <option value="">—</option>
                  {STONE_WORK_TYPES.map((c) => <option key={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Stone colour</Form.Label>
                <Form.Control placeholder="e.g. Ruby red" value={form.stoneColour} onChange={set('stoneColour')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Pearl colour</Form.Label>
                <Form.Control placeholder="e.g. Off-white" value={form.pearlColour} onChange={set('pearlColour')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Design style</Form.Label>
                <Form.Control placeholder="e.g. Temple, Polki, Contemporary" value={form.designStyle} onChange={set('designStyle')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Brand / maker</Form.Label>
                <Form.Control value={form.brand} onChange={set('brand')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Collection / design name</Form.Label>
                <Form.Control value={form.collectionName} onChange={set('collectionName')} />
              </Form.Group>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {dims.length > 0 && (
        <Accordion.Item eventKey="size">
          <Accordion.Header>Size &amp; fit <span className="text-muted ms-2 small">optional</span></Accordion.Header>
          <Accordion.Body>
            <p className="text-muted small">Only the measurements that matter for a {form.category.toLowerCase()} are shown.</p>
            <Row>{dims.map(renderDimField)}</Row>
          </Accordion.Body>
        </Accordion.Item>
      )}

      {showSet && (
        <Accordion.Item eventKey="set">
          <Accordion.Header>Set contents <span className="text-muted ms-2 small">optional</span></Accordion.Header>
          <Accordion.Body>
            <p className="text-muted small">Tick every component included with this set and set how many of each.</p>
            <Row xs={1} sm={2} className="g-2 mb-2">
              {SET_COMPONENTS.map((name) => {
                const checked = form.setComponents.some((c) => c.name === name);
                return (
                  <Col key={name}>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        id={`setcomp-${name}`}
                        label={name}
                        checked={checked}
                        onChange={toggleComponent(name)}
                      />
                      {checked && (
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          style={{ width: 72 }}
                          value={componentQty(name) ?? 1}
                          onChange={setComponentQty(name)}
                          aria-label={`${name} quantity`}
                        />
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
            <Form.Check
              type="checkbox"
              id="set-complete-only"
              label="This set can be rented only as a complete set (individual pieces not available)"
              checked={form.setCompleteOnly}
              onChange={setChecked('setCompleteOnly')}
            />
          </Accordion.Body>
        </Accordion.Item>
      )}

      <Accordion.Item eventKey="weight">
        <Accordion.Header>Weight <span className="text-muted ms-2 small">optional</span></Accordion.Header>
        <Accordion.Body>
          <Row className="align-items-end">
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                <Form.Label>Approx. weight</Form.Label>
                <Form.Control type="number" min={0} step="0.1" value={form.approxWeight} onChange={set('approxWeight')} />
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                <Form.Label>Unit</Form.Label>
                <Form.Select value={form.weightUnit} onChange={set('weightUnit')}>
                  {WEIGHT_UNITS.map((u) => <option key={u}>{u}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Form.Text className="text-muted d-block">
            This is the physical weight of the artificial / 1-gram piece — <strong>not</strong> the amount of gold it contains.
          </Form.Text>
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="condition">
        <Accordion.Header>Condition &amp; disclosures <span className="text-muted ms-2 small">optional</span></Accordion.Header>
        <Accordion.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Condition</Form.Label>
                <Form.Select value={form.condition} onChange={set('condition')}>
                  <option value="">—</option>
                  {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Label className="small mb-1 d-block">Insurance during rental</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {[
                  { value: '', label: 'Not insured' },
                  { value: 'inclusive', label: 'Insured — price inclusive' },
                  { value: 'exclusive', label: 'Insured — price exclusive' },
                ].map(({ value, label }) => (
                  <Form.Check key={value} type="radio" id={`insured-${value || 'none'}`}
                    label={label} name="insurancePricing"
                    checked={form.insurancePricing === value}
                    onChange={() => set('insurancePricing')({ target: { value } })} />
                ))}
              </div>
              <Form.Text muted>
                Inclusive = insurance cost is already in the rental price. Exclusive = charged separately.
              </Form.Text>
            </Col>
          </Row>
          <p className="text-muted small mb-2">Disclose any existing defects up front — this is shown to the customer before they book.</p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Visible scratches?</Form.Label>
                <Form.Control placeholder="e.g. None / light scratch on clasp" value={form.scratches} onChange={set('scratches')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Missing stones?</Form.Label>
                <Form.Control placeholder="e.g. None / 1 stone missing near centre" value={form.missingStones} onChange={set('missingStones')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Repair history</Form.Label>
                <Form.Control placeholder="e.g. Clasp re-soldered in 2025" value={form.repairHistory} onChange={set('repairHistory')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Other defects</Form.Label>
                <Form.Control value={form.otherDefects} onChange={set('otherDefects')} />
              </Form.Group>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// Read-only view for the product detail page.
// ---------------------------------------------------------------------------
function group(title, rows) {
  const kept = rows.filter(([, v]) => v !== '' && v != null && v !== false);
  return kept.length ? { title, rows: kept } : null;
}

export function JewellerySpecView({ item }) {
  const a = item?.attributes || {};
  const groups = [
    group('Appearance & make', [
      ['Primary colour', a.primaryColour || a.metal],
      ['Finish', a.finish],
      ['Stone / work type', a.stoneWorkType],
      ['Stone colour', a.stoneColour],
      ['Pearl colour', a.pearlColour],
      ['Design style', a.designStyle],
      ['Brand / maker', a.brand],
      ['Collection', a.collectionName]
    ]),
    group('Size & fit', [
      ['Length', a.dimLength],
      ['Width', a.dimWidth],
      ['Adjustable', a.dimAdjustable],
      ['Bangle size', a.bangleSize],
      ['Inner diameter', a.innerDiameter],
      ['Ring size', a.ringSize],
      ['Pendant size', a.pendantSize],
      ['Chain length', a.chainLength],
      ['Diameter', a.diameter],
      ['Weight', a.earringWeight]
    ]),
    group('Weight', [
      ['Approx. weight', a.approxWeight != null && a.approxWeight !== '' ? `${a.approxWeight} ${a.weightUnit || 'g'}` : '']
    ]),
    group('Condition & disclosures', [
      ['Condition', a.condition],
      ['Insurance', a.insurancePricing === 'inclusive' ? 'Covered — price inclusive'
        : a.insurancePricing === 'exclusive' ? 'Covered — price exclusive'
        : a.insured ? 'Covered during rental' : ''],
      ['Visible scratches', a.scratches],
      ['Missing stones', a.missingStones],
      ['Repair history', a.repairHistory],
      ['Other defects', a.otherDefects]
    ])
  ].filter(Boolean);

  const setComponents = Array.isArray(a.setComponents) ? a.setComponents : [];

  if (groups.length === 0 && setComponents.length === 0) return null;

  return (
    <div className="mb-3">
      {setComponents.length > 0 && (
        <div className="mb-3">
          <h5>What's included</h5>
          <ul className="mb-1">
            {setComponents.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
          {a.setCompleteOnly && (
            <Badge bg="secondary" className="fw-normal">Rented as a complete set only</Badge>
          )}
        </div>
      )}
      {groups.map((g) => (
        <div className="mb-3" key={g.title}>
          <h5>{g.title}</h5>
          <Table size="sm" bordered className="mb-1" style={{ maxWidth: 480 }}>
            <tbody>
              {g.rows.map(([label, value]) => (
                <tr key={label}>
                  <th className="text-muted fw-normal" style={{ width: '45%' }}>{label}</th>
                  <td>{String(value)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {g.title === 'Weight' && (
            <Form.Text className="text-muted">Physical weight of the artificial piece, not gold content.</Form.Text>
          )}
        </div>
      ))}
    </div>
  );
}

// Short highlight tags for the listing card (capped by the caller).
export function jewelleryHighlightTags(item) {
  const a = item?.attributes || {};
  const tags = [];
  const colour = a.primaryColour || a.metal;
  if (colour) tags.push(a.carat ? `${a.carat}K ${colour}` : colour);
  if (a.stoneWorkType) tags.push(a.stoneWorkType);
  if (a.finish) tags.push(a.finish);
  if (a.handmade) tags.push('Handmade');
  if (Array.isArray(a.setComponents) && a.setComponents.length) tags.push(`Set of ${a.setComponents.length}`);
  if (item?.category) tags.push(item.category);
  if (a.insured) tags.push('Insured');
  return tags;
}
